import { __awaiter, __extends, __generator } from "tslib";
import { FailedReason, SdkMessageTypes } from '../constants';
import { AppInitState } from '../hosted-web-content-sdk.interface';
import { transformContext } from '../utils';
import { getCurrentFeatureFlagsState } from '../utils/featureFlags';
import { isHostAdaptiveCardSchemaVersionUnsupported } from '../utils/helpers';
import { BaseMessageHandler } from './base-message-handler';
import { downgradeHubConfig } from './hub-config';
import { formulateAppMessageResult, logMessageResult } from './messageHandlingUtils';
var APP_LOAD_TIMEOUT = 30000; // 30 seconds
var INITIALIZE_TIMEOUT = 15000; // 15 seconds
/**
 * Responsible for handling app initialization related message requests.
 */
var AppMessageHandler = /** @class */ (function (_super) {
    __extends(AppMessageHandler, _super);
    function AppMessageHandler(appSdkHost, hubServices, telemetryService, hostVersionsInfo) {
        var _this = this;
        var _a, _b, _c, _d;
        _this = _super.call(this, appSdkHost, telemetryService) || this;
        _this.appSdkHost = appSdkHost;
        _this.hubServices = hubServices;
        _this.telemetryService = telemetryService;
        _this.hostVersionsInfo = hostVersionsInfo;
        _this.initializationStatusReported = false;
        _this.setupAppLoadTimer = function () {
            var _a;
            var timeout = (_a = getCurrentFeatureFlagsState().extendedAppLoadTimeoutInMillis) !== null && _a !== void 0 ? _a : APP_LOAD_TIMEOUT;
            if (_this.registerAppInitMessageHandlers) {
                return window.setTimeout(function () {
                    _this.appSdkHost.setAppInitInfo({
                        state: AppInitState.Failed,
                        reason: FailedReason.AppInitTimeout,
                    });
                    _this.failAppInitializationScenario('App Load timed out');
                }, timeout);
            }
            else {
                return window.setTimeout(function () {
                    _this.failAppInitializationScenario('SDK initialization timed out');
                }, INITIALIZE_TIMEOUT);
            }
        };
        _this.stopAppInitializationScenario = function () {
            var _a;
            _this.clearTimeoutTimer();
            (_a = _this.appInitializationScenario) === null || _a === void 0 ? void 0 : _a.stop();
        };
        _this.failAppInitializationScenario = function (reason) {
            var _a;
            _this.clearTimeoutTimer();
            (_a = _this.appInitializationScenario) === null || _a === void 0 ? void 0 : _a.fail(reason);
        };
        _this.clearTimeoutTimer = function () {
            window.clearTimeout(_this.appLoadTimerId);
        };
        // start and get appInit scenario
        _this.appSdkHost.startAppInitializationScenario();
        _this.appInitializationScenario = (_a = _this.appSdkHost) === null || _a === void 0 ? void 0 : _a.getAppInitializationScenario();
        (_b = _this.appInitializationScenario) === null || _b === void 0 ? void 0 : _b.markPhase('ClickToBeginLoad');
        _this.registerAppInitMessageHandlers = ((_c = appSdkHost.getAppDefinition()) === null || _c === void 0 ? void 0 : _c.showLoadingIndicator) || false;
        if (telemetryService === null || telemetryService === void 0 ? void 0 : telemetryService.enableExtendedInitializationTracking) {
            _this.extendedAppInitializationScenario = (_d = _this.telemetryService) === null || _d === void 0 ? void 0 : _d.createScenario('appInitializationExtended');
        }
        _this.appLoadTimerId = _this.setupAppLoadTimer();
        return _this;
    }
    AppMessageHandler.prototype.initHandlers = function () {
        this.registerHandler(SdkMessageTypes.initialize, this.initialize);
        this.registerHandler(SdkMessageTypes.getContext, this.getContext, {
            errorCallback: this.onGetContextError,
        });
        this.registerHandler(SdkMessageTypes.readyToUnload, this.readyToUnload);
        this.registerHandler(SdkMessageTypes.appInitialization.appLoaded, this.handleAppLoaded);
        this.registerHandler(SdkMessageTypes.appInitialization.success, this.handleSuccess);
        this.registerHandler(SdkMessageTypes.appInitialization.failure, this.handleFailure);
        this.registerHandler(SdkMessageTypes.appInitialization.expectedFailure, this.handleExpectedFailure);
    };
    /**
     * Initialize communication between sdk service and hosted content
     */
    AppMessageHandler.prototype.initialize = function (_, appSdkVersion, appSdkRuntimeVersion) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (!appSdkRuntimeVersion) {
            appSdkRuntimeVersion = 1;
        }
        (_a = this.appInitializationScenario) === null || _a === void 0 ? void 0 : _a.markPhase('BeginLoadToInitialized');
        (_b = this.extendedAppInitializationScenario) === null || _b === void 0 ? void 0 : _b.markPhase('BeginLoadToInitialized');
        (_c = this.telemetryService) === null || _c === void 0 ? void 0 : _c.setAppSdkVersion(appSdkVersion);
        if (!this.registerAppInitMessageHandlers) {
            // if the application is not using the appInit messages, we use initialize to transition to loaded state
            // and stop the app load telemetry scenario
            this.appSdkHost.setAppInitInfo({ state: AppInitState.Loaded });
            this.stopAppInitializationScenario();
        }
        this.appSdkHost.setInitialized(true);
        var hubConfig = {
            apiVersion: 2,
            hostVersionsInfo: this.hostVersionsInfo,
            supports: {
                app: {},
                appInstallDialog: this.hubServices.appInstallDialogService ? {} : undefined,
                appEntity: this.hubServices.appEntityService ? {} : undefined,
                barCode: this.hubServices.barCodeService ? {} : undefined,
                calendar: this.hubServices.calendarService ? {} : undefined,
                call: this.hubServices.callService ? {} : undefined,
                chat: this.hubServices.chatService ? {} : undefined,
                conversations: this.hubServices.conversationsService ? {} : undefined,
                dialog: this.hubServices.dialogService
                    ? {
                        url: this.hubServices.dialogService.urlService ? {} : undefined,
                        card: this.hubServices.dialogService.cardService &&
                            this.hostVersionsInfo &&
                            this.hostVersionsInfo.adaptiveCardSchemaVersion &&
                            !isHostAdaptiveCardSchemaVersionUnsupported(this.hostVersionsInfo.adaptiveCardSchemaVersion)
                            ? {}
                            : undefined,
                        update: this.hubServices.dialogService.updateService ? {} : undefined,
                    }
                    : undefined,
                files: this.hubServices.filesService ? {} : undefined,
                interactive: this.hubServices.interactiveService ? {} : undefined,
                mail: this.hubServices.mailService ? {} : undefined,
                media: this.hubServices.mediaService ? {} : undefined,
                meeting: this.hubServices.meetingService
                    ? {
                        appShareButton: this.hubServices.meetingService.appShareButton ? {} : undefined,
                    }
                    : undefined,
                meetingRoom: this.hubServices.meetingRoomService ? {} : undefined,
                notifications: this.hubServices.notificationService ? {} : undefined,
                location: this.hubServices.locationService && this.hubServices.locationService.mapService ? {} : undefined,
                geoLocation: this.hubServices.locationService
                    ? {
                        map: this.hubServices.locationService.mapService ? {} : undefined,
                    }
                    : undefined,
                logs: this.hubServices.logService ? {} : undefined,
                pages: {
                    appButton: ((_d = this.hubServices.pageService) === null || _d === void 0 ? void 0 : _d.appButtonService) ? {} : undefined,
                    tabs: ((_e = this.hubServices.pageService) === null || _e === void 0 ? void 0 : _e.tabsService) ? {} : undefined,
                    config: ((_f = this.hubServices.pageService) === null || _f === void 0 ? void 0 : _f.configService) ? {} : undefined,
                    backStack: ((_g = this.hubServices.pageService) === null || _g === void 0 ? void 0 : _g.backStackService) ? {} : undefined,
                    fullTrust: ((_h = this.hubServices.pageService) === null || _h === void 0 ? void 0 : _h.fullTrustService) ? {} : undefined,
                    //Note: currentApp is a sub capability which internally calls pages.navigateToApp
                    //and uses pageService therefore we don't check for any service and just
                    //defaults to {}
                    currentApp: {},
                },
                people: this.hubServices.peopleService ? {} : undefined,
                permissions: this.hubServices.devicePermissionService ? {} : undefined,
                profile: this.hubServices.profileService ? {} : undefined,
                remoteCamera: this.hubServices.remoteCameraService ? {} : undefined,
                monetization: this.hubServices.monetizationService ? {} : undefined,
                search: this.hubServices.searchService ? {} : undefined,
                sharing: this.hubServices.sharingService ? {} : undefined,
                stageView: this.hubServices.stageViewService ? {} : undefined,
                teams: this.hubServices.teamsService
                    ? {
                        fullTrust: ((_j = this.hubServices.teamsService) === null || _j === void 0 ? void 0 : _j.fullTrustService)
                            ? {
                                joinedTeams: ((_l = (_k = this.hubServices.teamsService) === null || _k === void 0 ? void 0 : _k.fullTrustService) === null || _l === void 0 ? void 0 : _l.joinedTeamsService) ? {} : undefined,
                            }
                            : undefined,
                    }
                    : undefined,
                teamsCore: this.hubServices.cacheManagementService ? {} : undefined,
            },
        };
        var hubConfigToSend;
        // Use older version of hubConfig if the App SDK runtime apiVersion is lower than the hubConfig apiVersion
        if (appSdkRuntimeVersion < hubConfig.apiVersion) {
            hubConfigToSend = downgradeHubConfig(hubConfig, appSdkRuntimeVersion);
        }
        else {
            hubConfigToSend = hubConfig;
        }
        var data = [
            this.appSdkHost.getFrameContext(),
            this.appSdkHost.getHubInfo().clientType,
            '2.0.0',
            JSON.stringify(hubConfigToSend),
        ];
        return formulateAppMessageResult(data);
    };
    /**
     * Retrieve the context of the SDK Provider
     */
    AppMessageHandler.prototype.getContext = function () {
        return __awaiter(this, void 0, void 0, function () {
            var data, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = transformContext;
                        return [4 /*yield*/, this.appSdkHost.getAppContext()];
                    case 1:
                        data = _a.apply(void 0, [_b.sent()]);
                        return [2 /*return*/, formulateAppMessageResult(data)];
                }
            });
        });
    };
    AppMessageHandler.prototype.onGetContextError = function (error, scenario) {
        // Note: getContext can never fail. We might want to change this now that we're moving to promises in
        // the App SDK but there may be back-compat concerns that we should investigate first.
        scenario === null || scenario === void 0 ? void 0 : scenario.fail(error);
        var data = {};
        return formulateAppMessageResult(data);
    };
    AppMessageHandler.prototype.handleAppLoaded = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                (_a = this.appInitializationScenario) === null || _a === void 0 ? void 0 : _a.markPhase('InitializedToAppLoadedInvoked');
                this.appSdkHost.setAppInitInfo({ state: AppInitState.Loaded });
                return [2 /*return*/, logMessageResult];
            });
        });
    };
    AppMessageHandler.prototype.handleSuccess = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                if (!this.markInitializationStatusAsReported()) {
                    return [2 /*return*/, logMessageResult];
                }
                this.appSdkHost.setAppInitInfo({ state: AppInitState.Loaded });
                if (this.registerAppInitMessageHandlers) {
                    this.stopAppInitializationScenario();
                }
                (_a = this.extendedAppInitializationScenario) === null || _a === void 0 ? void 0 : _a.stop();
                return [2 /*return*/, logMessageResult];
            });
        });
    };
    AppMessageHandler.prototype.handleFailure = function (_event, reason) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                if (!this.markInitializationStatusAsReported()) {
                    return [2 /*return*/, logMessageResult];
                }
                this.appSdkHost.setAppInitInfo({ state: AppInitState.Failed, reason: reason });
                if (this.registerAppInitMessageHandlers) {
                    this.failAppInitializationScenario(reason);
                }
                (_a = this.extendedAppInitializationScenario) === null || _a === void 0 ? void 0 : _a.fail(reason);
                return [2 /*return*/, logMessageResult];
            });
        });
    };
    AppMessageHandler.prototype.handleExpectedFailure = function (_event, reason) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                if (!this.markInitializationStatusAsReported()) {
                    return [2 /*return*/, logMessageResult];
                }
                this.appSdkHost.setAppInitInfo({ state: AppInitState.Loaded });
                if (this.registerAppInitMessageHandlers) {
                    this.failAppInitializationScenario('Expected.' + reason);
                }
                (_a = this.extendedAppInitializationScenario) === null || _a === void 0 ? void 0 : _a.fail('Expected.' + reason);
                return [2 /*return*/, logMessageResult];
            });
        });
    };
    AppMessageHandler.prototype.markInitializationStatusAsReported = function () {
        if (this.initializationStatusReported) {
            return false;
        }
        this.initializationStatusReported = true;
        return true;
    };
    AppMessageHandler.prototype.readyToUnload = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.appSdkHost.setAppInitInfo({ state: AppInitState.Unloaded });
                return [2 /*return*/, logMessageResult];
            });
        });
    };
    AppMessageHandler.prototype.cleanup = function () {
        this.clearTimeoutTimer();
    };
    return AppMessageHandler;
}(BaseMessageHandler));
export { AppMessageHandler };
//# sourceMappingURL=app-message-handler.js.map